import React, { useState, useMemo, useEffect } from 'react';
import { DndContext, DragOverlay, useDraggable, useDroppable, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { updateOrder, FULFILMENT, fmtMoney, fmtDate } from './productCommerce';
import ProductOrderDetail from './ProductOrderDetail';
import { productService } from '../../_services/product.service';
import './ProductV2.css';

// Online product orders + fulfilment. A status board (Kanban) by default with
// drag-to-advance, plus a classic table view a toggle away. Opening a card shows
// the detail slide-out. Mock-first.

const FILTERS = [
  { id: 'all', label: 'All' },
  { id: 'tofulfil', label: 'To fulfil' },
  { id: 'shipped', label: 'Shipped' },
  { id: 'completed', label: 'Completed' },
];

const BOARD_COLUMNS = [
  { id: 'tofulfil',   label: 'To fulfil',            statuses: ['unfulfilled', 'packed'], tone: 'amber' },
  { id: 'collection', label: 'Ready for collection', statuses: ['ready_for_collection'],  tone: 'blue' },
  { id: 'shipped',    label: 'Shipped',              statuses: ['shipped'],               tone: 'violet' },
  { id: 'completed',  label: 'Completed',            statuses: ['delivered', 'collected'], tone: 'green' },
  { id: 'cancelled',  label: 'Cancelled',            statuses: ['cancelled'],             tone: 'grey' },
];

const PRIORITY = { unfulfilled: 0, ready_for_collection: 1, packed: 2, shipped: 3, delivered: 4, collected: 4, cancelled: 5 };

const inFilter = (o, filter) => {
  if (filter === 'all') return true;
  if (filter === 'tofulfil') return ['unfulfilled', 'packed', 'ready_for_collection'].includes(o.fulfilmentStatus);
  if (filter === 'shipped') return o.fulfilmentStatus === 'shipped';
  if (filter === 'completed') return ['delivered', 'collected'].includes(o.fulfilmentStatus);
  return true;
};

// Where a card lands when dropped in a column — depends on the delivery method,
// so a ship order can't become "ready for collection" and vice versa.
const dropTargetStatus = (colId, method) => {
  switch (colId) {
    case 'tofulfil':   return 'unfulfilled';
    case 'collection': return method === 'collect' ? 'ready_for_collection' : null;
    case 'shipped':    return method === 'ship' ? 'shipped' : null;
    case 'completed':  return method === 'collect' ? 'collected' : 'delivered';
    case 'cancelled':  return 'cancelled';
    default: return null;
  }
};
const toneOf = (o) => (BOARD_COLUMNS.find((c) => c.statuses.includes(o.fulfilmentStatus)) || {}).tone || 'grey';

const itemSummary = (o) => {
  const items = o.items || [];
  if (items.length === 0) return '—';
  if (items.length === 1) return `${items[0].qty}× ${items[0].name}`;
  return `${items[0].name} +${items.length - 1} more`;
};

const BoardIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden><rect x="3" y="4" width="5" height="16" rx="1" /><rect x="10" y="4" width="5" height="11" rx="1" /><rect x="17" y="4" width="4" height="7" rx="1" /></svg>
);
const TableIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden><line x1="3" y1="6" x2="21" y2="6" /><line x1="3" y1="12" x2="21" y2="12" /><line x1="3" y1="18" x2="21" y2="18" /></svg>
);
const ShipIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden><path d="M3 7h11v8H3zM14 10h4l3 3v2h-7z" /><circle cx="7" cy="18" r="1.6" /><circle cx="17.5" cy="18" r="1.6" /></svg>
);
const CollectIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden><path d="M6 8h12l-1 11a1 1 0 0 1-1 1H8a1 1 0 0 1-1-1L6 8zM9 8V6a3 3 0 0 1 6 0v2" /></svg>
);

const RefundPill = ({ o }) => (
  (o.paymentStatus === 'refunded' || o.paymentStatus === 'partially_refunded')
    ? <span className="pv2-pill pv2-pill--grey">{o.paymentStatus === 'refunded' ? 'Refunded' : 'Part refunded'}</span>
    : null
);

const DeliveryChip = ({ collect }) => (
  <span className={`pv2-deliv-chip pv2-deliv-chip--${collect ? 'collect' : 'ship'}`}>
    {collect ? <CollectIcon /> : <ShipIcon />}{collect ? 'Collect' : 'Ship'}
  </span>
);

const CardInner = ({ o, currency }) => {
  const collect = o.delivery && o.delivery.method === 'collect';
  return (
    <>
      <div className="pv2-board-card-items">{itemSummary(o)}</div>
      <div className="pv2-board-card-sub">{o.number} · {o.customer.name}</div>
      <div className="pv2-board-card-foot">
        <DeliveryChip collect={collect} />
        <span className="pv2-board-card-when">{fmtDate(o.createdAt)}</span>
        <span className="pv2-board-card-total">{fmtMoney(o.total, currency)}</span>
      </div>
      {(o.paymentStatus === 'refunded' || o.paymentStatus === 'partially_refunded') && (
        <div className="pv2-board-card-refund"><RefundPill o={o} /></div>
      )}
    </>
  );
};

const BoardCard = ({ o, tone, currency, onOpen }) => {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({ id: o.id });
  return (
    <div
      ref={setNodeRef}
      className={`pv2-board-card pv2-board-card--${tone}${isDragging ? ' is-dragging' : ''}`}
      onClick={() => onOpen(o.id)}
      {...attributes}
      {...listeners}
    >
      <CardInner o={o} currency={currency} />
    </div>
  );
};

const BoardColumn = ({ col, cards, currency, onOpen, activeOrder }) => {
  const { setNodeRef, isOver } = useDroppable({ id: col.id });
  let state = '';
  if (activeOrder) {
    const isSource = col.statuses.includes(activeOrder.fulfilmentStatus);
    const target = dropTargetStatus(col.id, activeOrder.delivery && activeOrder.delivery.method);
    if (!isSource) state = target ? 'is-droppable' : 'is-blocked';
  }
  return (
    <div className="pv2-board-col">
      <div className="pv2-board-col-head">
        <span className={`pv2-board-dot pv2-board-dot--${col.tone}`} />
        <span className="pv2-board-col-title">{col.label}</span>
        <span className="pv2-board-col-count">{cards.length}</span>
      </div>
      <div ref={setNodeRef} className={`pv2-board-col-body ${state}${isOver ? ' is-over' : ''}`}>
        {cards.length === 0 ? (
          <div className="pv2-board-empty">{isOver && state === 'is-droppable' ? 'Drop to move here' : 'Nothing here'}</div>
        ) : cards.map((o) => <BoardCard key={o.id} o={o} tone={col.tone} currency={currency} onOpen={onOpen} />)}
      </div>
    </div>
  );
};

const ProductOrders = ({ currency = '£', onCountChange }) => {
  const [orders, setOrders] = useState([]);
  const [view, setView] = useState('board'); // 'board' | 'table'
  const [filter, setFilter] = useState('all');
  const [query, setQuery] = useState('');
  const [activeId, setActiveId] = useState(null);     // order open in the detail panel
  const [activeOrder, setActiveOrder] = useState(null); // order currently being dragged

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));

  // Load real orders from the backend.
  useEffect(() => {
    let alive = true;
    productService.listOrders().then((list) => { if (alive) setOrders(list || []); }).catch(() => {});
    return () => { alive = false; };
  }, []);

  const needsCount = useMemo(() => orders.filter((o) => o.fulfilmentStatus === 'unfulfilled').length, [orders]);
  useEffect(() => { if (onCountChange) onCountChange(needsCount); }, [needsCount, onCountChange]);

  const handleUpdate = (id, patch, label) => {
    // Optimistic local update for instant UI…
    const next = updateOrder(orders, id, patch, label);
    setOrders([...next]);
    // …then persist to the backend (fulfilment status / tracking / internal note).
    productService.updateOrder(id, patch).catch((err) => {
      console.error('Order update failed, refreshing from server', err);
      productService.listOrders().then((list) => setOrders(list || [])).catch(() => {});
    });
  };

  // Cancel goes through the real cancel endpoint (refund + payout reversal on
  // the BE) — NOT the optimistic patch path: the money must succeed first.
  const handleCancel = (id, reason) => {
    return productService.cancelOrder(id, reason).then((result) => {
      productService.listOrders().then((list) => setOrders(list || [])).catch(() => {});
      return result;
    });
  };

  const matchesQuery = (o, q) => !q || o.number.toLowerCase().includes(q)
    || o.customer.name.toLowerCase().includes(q) || o.customer.email.toLowerCase().includes(q);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return orders.filter((o) => inFilter(o, filter)).filter((o) => matchesQuery(o, q))
      .sort((a, b) => {
        const p = (PRIORITY[a.fulfilmentStatus] ?? 9) - (PRIORITY[b.fulfilmentStatus] ?? 9);
        return p !== 0 ? p : (a.createdAt < b.createdAt ? 1 : -1);
      });
  }, [orders, filter, query]);

  const searched = useMemo(() => {
    const q = query.trim().toLowerCase();
    return orders.filter((o) => matchesQuery(o, q)).sort((a, b) => (a.createdAt < b.createdAt ? 1 : -1));
  }, [orders, query]);

  const active = orders.find((o) => o.id === activeId) || null;
  const counts = {
    all: orders.length,
    tofulfil: orders.filter((o) => inFilter(o, 'tofulfil')).length,
    shipped: orders.filter((o) => inFilter(o, 'shipped')).length,
    completed: orders.filter((o) => inFilter(o, 'completed')).length,
  };

  const onDragStart = (e) => setActiveOrder(orders.find((o) => o.id === e.active.id) || null);
  const onDragEnd = (e) => {
    setActiveOrder(null);
    if (!e.over) return;
    const order = orders.find((o) => o.id === e.active.id);
    if (!order) return;
    const target = dropTargetStatus(e.over.id, order.delivery && order.delivery.method);
    if (!target || target === order.fulfilmentStatus) return;
    handleUpdate(order.id, { fulfilmentStatus: target }, `Moved to ${(FULFILMENT[target].label || '').toLowerCase()}`);
  };

  return (
    <div className="pv2-orders">
      <div className="pv2-orders-toolbar">
        <div className="pv2-orders-toolbar-left">
          <div className="pv2-viewtoggle" role="tablist" aria-label="View">
            <button type="button" role="tab" aria-selected={view === 'board'} className={`pv2-viewtoggle-btn ${view === 'board' ? 'active' : ''}`} onClick={() => setView('board')}><BoardIcon /> Board</button>
            <button type="button" role="tab" aria-selected={view === 'table'} className={`pv2-viewtoggle-btn ${view === 'table' ? 'active' : ''}`} onClick={() => setView('table')}><TableIcon /> Table</button>
          </div>
          {view === 'table' && (
            <div className="pv2-filter-chips">
              {FILTERS.map((f) => (
                <button key={f.id} type="button" className={`pv2-filter-chip ${filter === f.id ? 'active' : ''}`} onClick={() => setFilter(f.id)}>
                  {f.label}{counts[f.id] > 0 && <span className="pv2-filter-chip-count">{counts[f.id]}</span>}
                </button>
              ))}
            </div>
          )}
        </div>
        <label className="pv2-search">
          <span className="pv2-search-icon" aria-hidden>
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"><circle cx="7" cy="7" r="5" /><path d="M10.5 10.5L14 14" /></svg>
          </span>
          <input type="text" placeholder="Search order or customer…" value={query} onChange={(e) => setQuery(e.target.value)} />
        </label>
      </div>

      {view === 'board' ? (
        <DndContext sensors={sensors} onDragStart={onDragStart} onDragEnd={onDragEnd} onDragCancel={() => setActiveOrder(null)}>
          <div className="pv2-board">
            {BOARD_COLUMNS.map((col) => (
              <BoardColumn
                key={col.id}
                col={col}
                cards={searched.filter((o) => col.statuses.includes(o.fulfilmentStatus))}
                currency={currency}
                onOpen={setActiveId}
                activeOrder={activeOrder}
              />
            ))}
          </div>
          <DragOverlay dropAnimation={null}>
            {activeOrder ? (
              <div className={`pv2-board-card pv2-board-card--${toneOf(activeOrder)} pv2-board-card--overlay`}>
                <CardInner o={activeOrder} currency={currency} />
              </div>
            ) : null}
          </DragOverlay>
        </DndContext>
      ) : filtered.length === 0 ? (
        <div className="pv2-stub">
          <div className="pv2-stub-title">No orders here</div>
          <p className="pv2-stub-sub">Online orders will appear here to pack, ship, and track.</p>
        </div>
      ) : (
        <div className="pv2-table-wrap">
          <table className="pv2-table">
            <thead>
              <tr><th>Order</th><th>Date</th><th>Customer</th><th>Items</th><th>Delivery</th><th className="pv2-th-right">Total</th><th>Status</th></tr>
            </thead>
            <tbody>
              {filtered.map((o) => {
                const f = FULFILMENT[o.fulfilmentStatus] || FULFILMENT.unfulfilled;
                const itemCount = o.items.reduce((s, it) => s + it.qty, 0);
                return (
                  <tr key={o.id} role="button" tabIndex={0} onClick={() => setActiveId(o.id)} onKeyDown={(e) => { if (e.key === 'Enter') setActiveId(o.id); }}>
                    <td className="pv2-td-strong">{o.number}</td>
                    <td className="pv2-td-muted">{fmtDate(o.createdAt)}</td>
                    <td>
                      <div className="pv2-td-strong">{o.customer.name}</div>
                      <div className="pv2-td-sub">{o.customer.email}</div>
                    </td>
                    <td className="pv2-td-muted">{itemCount} item{itemCount === 1 ? '' : 's'}</td>
                    <td><DeliveryChip collect={o.delivery && o.delivery.method === 'collect'} /></td>
                    <td className="pv2-td-right pv2-td-strong">{fmtMoney(o.total, currency)}</td>
                    <td>
                      <div className="pv2-td-status">
                        <span className={`pv2-pill pv2-pill--${f.tone}`}>{f.label}</span>
                        <RefundPill o={o} />
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {active && (
        <ProductOrderDetail order={active} currency={currency} onClose={() => setActiveId(null)} onUpdate={handleUpdate} onCancel={handleCancel} />
      )}
    </div>
  );
};

export default ProductOrders;
